import React from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";

export function AuditModule({
  t,
  lang,
  hasXKey,
  // Audit state
  auditTimeRange,
  setAuditTimeRange,
  auditCustomDays,
  setAuditCustomDays,
  auditPageSize,
  setAuditPageSize,
  auditLogs,
  loadingAudit,
  auditNextPageToken,
  auditSelectedItems,
  auditSelectAll,
  auditSortField,
  auditSortDirection,
  // Functions
  loadAuditLogs,
  sortAuditLogs,
  handleSelectAll,
  handleSelectItem,
  exportSelected,
  clearSelection,
  formatTimestamp
}) {
  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
      
      {/* Audit Header */}
      <Card className="panel lightCard" style={{marginTop: 8}}>
        <CardContent>
          <div className="panel-title darktxt">🔍 {t('audit_title')}</div>
          <div className="small">{t('audit_subtitle')}</div>
        </CardContent>
      </Card>

      {/* Audit Controls */}
      <Card className="panel lightCard">
        <CardContent>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
            <div className="panel-title darktxt">{t('audit_time_range')}</div>
            <Button 
              onClick={() => loadAuditLogs(auditTimeRange, auditPageSize)} 
              disabled={loadingAudit}
              size="sm"
              variant="secondary"
            >
              {loadingAudit ? t('audit_loading') : '🔄 ' + t('audit_refresh')}
            </Button>
          </div>
          
          {/* Time Range Buttons */}
          <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px'}}>
            {[
              {value: '24h', label: t('audit_last_24h')},
              {value: '7d', label: t('audit_last_7d')},
              {value: '30d', label: t('audit_last_30d')},
              {value: 'custom', label: `${t('audit_custom')} (${auditCustomDays} ${auditCustomDays === 1 ? t('audit_day') : t('audit_days')})`}
            ].map(range => (
              <Button
                key={range.value}
                onClick={() => {
                  setAuditTimeRange(range.value);
                  loadAuditLogs(range.value, auditPageSize);
                }}
                variant={auditTimeRange === range.value ? 'default' : 'secondary'}
                size="sm"
                disabled={loadingAudit}
              >
                {range.label}
              </Button>
            ))}
          </div>

          {/* Custom Days Slider - only show when custom is selected */}
          {auditTimeRange === 'custom' && (
            <div style={{marginBottom: '12px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e5e7eb'}}>
              <Label className="small" style={{marginBottom: '8px', display: 'block'}}>
                📅 {t('audit_time_range_label')}: {auditCustomDays} {auditCustomDays === 1 ? t('audit_day') : t('audit_days')} (max. 30)
              </Label>
              <input 
                type="range" 
                min="1" 
                max="30" 
                value={auditCustomDays}
                onChange={(e) => setAuditCustomDays(parseInt(e.target.value))}
                onMouseUp={() => loadAuditLogs('custom', auditPageSize)}
                style={{
                  width: '100%',
                  height: '8px',
                  borderRadius: '4px',
                  background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(auditCustomDays-1)/29*100}%, #d1d5db ${(auditCustomDays-1)/29*100}%, #d1d5db 100%)`,
                  outline: 'none',
                  WebkitAppearance: 'none',
                  cursor: 'pointer'
                }}
              />
              <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--muted)', marginTop: '4px'}}>
                <span>1 {t('audit_day')}</span>
                <span>15 {t('audit_days')}</span>
                <span>30 {t('audit_days')}</span>
              </div>
              <div style={{fontSize: '11px', color: 'var(--muted)', marginTop: '4px', textAlign: 'center'}}>
                {t('audit_slider_hint')}
              </div>
            </div>
          )}

          {/* Page Size and Status */}
          <div style={{display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap'}}>
            <Label className="small">{t('audit_page_size')}:</Label>
            <select 
              value={auditPageSize} 
              onChange={(e) => setAuditPageSize(parseInt(e.target.value))}
              style={{padding: '4px 8px', borderRadius: '4px', border: '1px solid #d1d5db'}}
            >
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
            
            {/* Status indicator */}
            <div style={{marginLeft: 'auto', fontSize: '11px', color: 'var(--muted)'}}>
              Status: {hasXKey ? `🔑 ${t('api_key_available')}` : `⚠️ ${t('api_key_missing')}`}
            </div>
          </div>

          {/* Selection Actions */}
          {auditSelectedItems.size > 0 && (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              backgroundColor: '#eff6ff',
              borderRadius: '6px',
              border: '1px solid #dbeafe',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap'
            }}>
              <div className="small" style={{color: '#1e40af', fontWeight: '500'}}>
                ✅ {t('audit_selected_count').replace('{}', auditSelectedItems.size)}
              </div>
              <div style={{display: 'flex', gap: '8px'}}>
                <Button size="sm" variant="secondary" onClick={exportSelected}>
                  📥 {t('audit_export_selected')}
                </Button>
                <Button size="sm" variant="outline" onClick={clearSelection}>
                  ❌ {t('audit_clear_selection')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit Logs Display */}
      <Card className="panel lightCard">
        <CardContent>
          
          {loadingAudit && (
            <div style={{
              backgroundColor: 'rgba(59, 130, 246, 0.1)', 
              padding: '16px', 
              borderRadius: '8px', 
              border: '1px solid rgba(59, 130, 246, 0.2)',
              textAlign: 'center'
            }}>
              <div className="small">
                🔄 {t('audit_loading')}
              </div>
            </div>
          )}

          {!loadingAudit && !auditLogs && (
            <div style={{
              backgroundColor: 'rgba(156, 163, 175, 0.1)', 
              padding: '16px', 
              borderRadius: '8px', 
              border: '1px solid rgba(156, 163, 175, 0.2)',
              textAlign: 'center'
            }}>
              <div className="small">
                📊 {t('audit_no_data')}
              </div>
              <div className="small" style={{marginTop: '8px', color: 'var(--muted)'}}>
                {t('click_refresh_audit')}
              </div>
            </div>
          )}

          {!loadingAudit && auditLogs && auditLogs.audit_logs && auditLogs.audit_logs.length > 0 && (
            <div>
              <div className="small" style={{marginBottom: '8px'}}>
                <strong>{t('audit_logs')}:</strong> {auditLogs.audit_logs.length} {t('entries')}
                {auditNextPageToken && <span style={{color: '#3b82f6'}}> ({t('more_available')})</span>}
              </div>
              
              {/* Permanent Hinweis für CSV Export */}
              <div className="small" style={{color: '#6b7280', fontStyle: 'italic', marginBottom: '8px'}}>
                💡 {t('hint_select_entries')}
              </div>
              
              {/* CSV Export Button */}
              <div style={{marginBottom: '12px', display: 'flex', gap: '8px', alignItems: 'center'}}>
                <Button
                  onClick={exportSelected}
                  variant="secondary"
                  size="sm"
                  disabled={auditSelectedItems.size === 0}
                  title={auditSelectedItems.size === 0 ? t('please_select_entries') : `${auditSelectedItems.size} ${t('export_entries')}`}
                >
                  📥 CSV Export ({auditSelectedItems.size} {lang === 'de' ? 'ausgewählt' : 'selected'})
                </Button>
                {auditSelectedItems.size > 0 && (
                  <Button
                    onClick={clearSelection}
                    variant="outline"
                    size="sm"
                  >
                    ❌ Auswahl löschen
                  </Button>
                )}
              </div>
              
              {/* Premium Audit Logs Table */}
              <div style={{
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                overflow: 'hidden',
                backgroundColor: 'white',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
              }}>
                <table style={{width: '100%', borderCollapse: 'collapse'}}>
                  <thead style={{backgroundColor: '#f8f9fa'}}>
                    <tr>
                      {/* Checkbox Column */}
                      <th style={{
                        textAlign: 'center', 
                        padding: '16px 8px', 
                        fontWeight: '600',
                        fontSize: '13px',
                        color: '#374151',
                        borderBottom: '2px solid #e5e7eb',
                        width: '50px'
                      }}>
                        <input
                          type="checkbox"
                          checked={auditSelectAll}
                          onChange={handleSelectAll}
                          style={{
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer'
                          }}
                          title={t('audit_select_all')}
                        />
                      </th>
                      
                      {[
                        {field: 'timestamp', label: t('audit_timestamp'), width: '180px'},
                        {field: 'user_name', label: t('audit_user'), width: '140px'},
                        {field: 'event_description', label: t('audit_event'), width: 'auto'},
                        {field: 'device_name', label: t('audit_device'), width: '140px'}
                      ].map(col => (
                        <th 
                          key={col.field}
                          onClick={() => sortAuditLogs(col.field)}
                          style={{
                            textAlign: 'left', 
                            padding: '16px 12px', 
                            fontWeight: '600',
                            fontSize: '13px',
                            color: '#374151',
                            borderBottom: '2px solid #e5e7eb',
                            cursor: 'pointer',
                            userSelect: 'none',
                            transition: 'background-color 0.2s',
                            width: col.width
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                          onMouseOut={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                        >
                          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            {col.label}
                            <span style={{
                              fontSize: '12px',
                              color: auditSortField === col.field ? '#3b82f6' : '#9ca3af',
                              transition: 'color 0.2s'
                            }}>
                              {auditSortField === col.field 
                                ? (auditSortDirection === 'asc' ? '↑' : '↓')
                                : '↕'
                              }
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.audit_logs.map((log, idx) => (
                      <tr key={idx} style={{
                        borderBottom: '1px solid #f3f4f6',
                        transition: 'background-color 0.2s',
                        backgroundColor: auditSelectedItems.has(idx) ? '#f0f9ff' : 'transparent'
                      }}
                      onMouseOver={(e) => e.target.parentNode.style.backgroundColor = auditSelectedItems.has(idx) ? '#e0f2fe' : '#f9fafb'}
                      onMouseOut={(e) => e.target.parentNode.style.backgroundColor = auditSelectedItems.has(idx) ? '#f0f9ff' : 'transparent'}
                      >
                        {/* Checkbox */}
                        <td style={{
                          padding: '14px 8px',
                          textAlign: 'center'
                        }}>
                          <input
                            type="checkbox"
                            checked={auditSelectedItems.has(idx)}
                            onChange={() => handleSelectItem(idx)}
                            style={{
                              width: '16px',
                              height: '16px',
                              cursor: 'pointer'
                            }}
                          />
                        </td>
                        {/* Timestamp */}
                        <td style={{
                          padding: '14px 12px',
                          fontSize: '13px',
                          fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace'
                        }}>
                          <div>
                            <div style={{fontWeight: '500', color: '#1f2937'}}>
                              {formatTimestamp(log.timestamp)}
                            </div>
                          </div>
                        </td>

                        {/* User Name */}
                        <td style={{padding: '14px 12px', fontSize: '13px'}}>
                          <div style={{
                            backgroundColor: '#eff6ff',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            color: '#1e40af',
                            display: 'inline-block',
                            border: '1px solid #dbeafe'
                          }}>
                            👤 {String(log.user_name || log.user_email || 'System')}
                          </div>
                        </td>

                        {/* Event Description */}
                        <td style={{padding: '14px 12px', fontSize: '13px'}}>
                          <div>
                            <div style={{
                              fontWeight: '500',
                              color: '#1f2937',
                              marginBottom: '2px'
                            }}>
                              {String(log.event_description || log.event_name || 'Unknown Event')}
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#6b7280',
                              maxWidth: '300px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {(() => {
                                if (log.ip_address) {
                                  return `IP: ${log.ip_address}`;
                                }
                                const content = log.details;
                                return typeof content === 'object' && content !== null
                                  ? `Details: ${JSON.stringify(content).substring(0, 60)}...`
                                  : String(content || 'Keine Details verfügbar');
                              })()}
                            </div>
                          </div>
                        </td>

                        {/* Device Name */}
                        <td style={{padding: '14px 12px', fontSize: '13px'}}>
                          <div style={{
                            backgroundColor: '#f0f9ff',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            color: '#0c4a6e',
                            display: 'inline-block',
                            border: '1px solid #e0f2fe'
                          }}>
                            🔧 {(() => {
                              if (log.devices && log.devices.length > 0) {
                                const device = log.devices[0];
                                return `${device.device_name || device.device_site_name || 'Unknown Device'}`;
                              }
                              return 'System';
                            })()}
                          </div>
                          {/* Show site name if different from device name */}
                          {log.devices && log.devices.length > 0 && log.devices[0].device_site_name && log.devices[0].device_name !== log.devices[0].device_site_name && (
                            <div style={{
                              fontSize: '11px',
                              color: '#6b7280',
                              marginTop: '2px'
                            }}>
                              📍 {log.devices[0].device_site_name}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {auditNextPageToken && (
                <div style={{marginTop: '12px', textAlign: 'center'}}>
                  <Button
                    onClick={() => loadAuditLogs(auditTimeRange, auditPageSize, auditNextPageToken)}
                    disabled={loadingAudit}
                    size="sm"
                    variant="secondary"
                  >
                    📄 {t('load_more_entries')}
                  </Button>
                </div>
              )}

            </div>
          )}

        </CardContent>
      </Card>

    </div>
  );
}
